import { createSlice } from '@reduxjs/toolkit';

/**
 * The reporting window shown in the app header, shared by every page that has
 * a period (Overview today; reports next). Keeping it in one slice is the
 * point — the old dashboard carried its own `time` select inside a card, and
 * each report page carried another, so two screens could disagree about which
 * month you were looking at.
 *
 * `preset` maps to the API's `time` param. A custom range instead sends
 * `startDate`/`endDate` with `useCustomRange`, which the summary endpoint
 * already supports.
 */
export const PERIOD_PRESETS = ['subWeek', 'subMonth', 'subYear'];

const initialState = {
  preset: 'subMonth',
  // ISO date strings, only meaningful when preset === 'custom'.
  startDate: null,
  endDate: null,
  granularity: 'daily',
  compare: true,
};

/** Translates the slice into the query params the admin API expects. */
export function periodToParams(period) {
  const base = {
    granularity: period?.granularity || 'daily',
    ...(period?.compare ? { compare: 'previous' } : {}),
  };

  if (period?.preset === 'custom' && period?.startDate && period?.endDate) {
    return {
      ...base,
      useCustomRange: true,
      startDate: period.startDate,
      endDate: period.endDate,
    };
  }

  return { ...base, time: period?.preset || 'subMonth' };
}

const periodSlice = createSlice({
  name: 'period',
  initialState,
  reducers: {
    setPreset(state, action) {
      const next = action.payload;
      state.preset = PERIOD_PRESETS.includes(next) ? next : 'subMonth';
      state.startDate = null;
      state.endDate = null;
      // A year of daily buckets is unreadable on a sparkline and slow to
      // aggregate, so the granularity follows the window unless overridden.
      state.granularity = state.preset === 'subYear' ? 'monthly' : 'daily';
    },
    setCustomRange(state, action) {
      const { startDate, endDate } = action.payload || {};
      if (!startDate || !endDate) return;
      state.preset = 'custom';
      state.startDate = startDate;
      state.endDate = endDate;
    },
    setGranularity(state, action) {
      state.granularity = action.payload || 'daily';
    },
    toggleCompare(state, action) {
      state.compare =
        typeof action.payload === 'boolean' ? action.payload : !state.compare;
    },
  },
});

export const { setPreset, setCustomRange, setGranularity, toggleCompare } =
  periodSlice.actions;
export default periodSlice.reducer;
